import React, { useEffect, useState } from 'react';
import api from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const role = (typeof window !== 'undefined' && localStorage.getItem('adminRole')) || '';
const isSuperAdmin = role && role.toUpperCase() === 'SUPER ADMIN';

const easternDateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short'
});

const formatCreatedAtEastern = (value) => {
    if (!value) return '';
    // Treat backend LocalDateTime (no timezone) as UTC for correct display
    let iso = value;
    if (!iso.endsWith('Z') && !iso.includes('+')) {
        iso += 'Z';
    }
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return String(value);
    return easternDateFormatter.format(dt);
};

const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 70 },
    { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 120 },
    { field: 'lastName', headerName: 'Last Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 180 },
    { field: 'role', headerName: 'Role', flex: 0.8, minWidth: 120 },
    { field: 'schoolName', headerName: 'School', flex: 1.5, minWidth: 180 },
    { field: 'otpVerified', headerName: 'OTP Verified', flex: 0.7, minWidth: 120, type: 'boolean' },
    {
        field: 'createdAt',
        headerName: 'Created At (ET)',
        flex: 1.2,
        minWidth: 210,
        renderCell: (params) => formatCreatedAtEastern(params.value)
    },
    {
        field: 'action',
        headerName: 'Actions',
        flex: 0,
        minWidth: 320,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    onClick={() => params.row.onVerifyOtp(params.row)}
                    disabled={!isSuperAdmin || Boolean(params.row.otpVerified)}
                >
                    Verify OTP
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => params.row.onDelete(params.row)}
                    disabled={!isSuperAdmin}
                    sx={!isSuperAdmin ? { backgroundColor: '#e0e0e0', color: '#888', border: 'none' } : {}}
                >
                    Delete
                </Button>
                {isSuperAdmin && (
                    <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => params.row.onResetTempPassword(params.row)}
                    >
                        Temp Password
                    </Button>
                )}
            </Box>
        ),
    },
];

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');
    const [otpFilter, setOtpFilter] = useState('All');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [selectedVerifyUser, setSelectedVerifyUser] = useState(null);
    const [tempPasswordDialogOpen, setTempPasswordDialogOpen] = useState(false);
    const [selectedTempPasswordUser, setSelectedTempPasswordUser] = useState(null);
    const [generatedTempPassword, setGeneratedTempPassword] = useState('');
    const [tempPasswordLoading, setTempPasswordLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/api/users');
            const data = res.data;
            // normalize role into `role` property for display (handle various backend shapes)
            setUsers(data.map(u => ({
                ...u,
                role: u.role || u.roleName || (u.roles && u.roles.length ? (u.roles[0].name || u.roles[0].role) : '') || '',
                onDelete: handleDeleteClick,
                onVerifyOtp: handleVerifyOtpClick,
                onResetTempPassword: handleResetTempPasswordClick
            })));
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to load users.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleVerifyOtpClick = (user) => {
        setSelectedVerifyUser(user);
        setVerifyDialogOpen(true);
    };

    const handleResetTempPasswordClick = (user) => {
        setSelectedTempPasswordUser(user);
        setGeneratedTempPassword('');
        setTempPasswordDialogOpen(true);
    };

    const handleVerifyOtpConfirm = async () => {
        if (!selectedVerifyUser) return;
        try {
            setLoading(true);
            await api.post(`/admin/api/users/${selectedVerifyUser.id}/verify-otp`);
            setSnackbar({ open: true, message: 'OTP manually verified.', severity: 'success' });
            await loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: typeof errMsg === 'string' ? errMsg : 'Failed to verify OTP.', severity: 'error' });
        } finally {
            setLoading(false);
            setVerifyDialogOpen(false);
            setSelectedVerifyUser(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUser) return;
        try {
            setLoading(true);
            await api.delete(`/admin/api/users/${selectedUser.id}`);
            setSnackbar({ open: true, message: 'User deleted successfully.', severity: 'success' });
            loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: typeof errMsg === 'string' ? errMsg : 'Failed to delete user.', severity: 'error' });
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
    };

    const handleVerifyDialogClose = () => {
        setVerifyDialogOpen(false);
        setSelectedVerifyUser(null);
    };

    const handleTempPasswordDialogClose = () => {
        setTempPasswordDialogOpen(false);
        setSelectedTempPasswordUser(null);
        setGeneratedTempPassword('');
    };

    const handleTempPasswordConfirm = async () => {
        if (!selectedTempPasswordUser) return;
        try {
            setTempPasswordLoading(true);
            const res = await api.post(`/admin/api/users/${selectedTempPasswordUser.id}/reset-temp-password`);
            const tempPassword = res.data?.temporaryPassword || '';
            setGeneratedTempPassword(tempPassword);
            if (!tempPassword) {
                setSnackbar({ open: true, message: 'Temporary password was generated but not returned.', severity: 'warning' });
            }
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: typeof errMsg === 'string' ? errMsg : 'Failed to generate temporary password.', severity: 'error' });
            handleTempPasswordDialogClose();
        } finally {
            setTempPasswordLoading(false);
        }
    };

    const handleCopyTempPassword = async () => {
        if (!generatedTempPassword) return;
        try {
            await navigator.clipboard.writeText(generatedTempPassword);
            setSnackbar({ open: true, message: 'Temporary password copied.', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Could not copy. Please copy manually.', severity: 'warning' });
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const roleOptions = React.useMemo(() => {
        const roles = new Set();
        users.forEach((u) => {
            if (u.role) roles.add(u.role);
        });
        return Array.from(roles).sort((a, b) => a.localeCompare(b));
    }, [users]);

    const filteredUsers = React.useMemo(() => {
        const q = search.trim().toLowerCase();

        return users.filter((u) => {
            const roleMatches = roleFilter === 'All' || (u.role || '') === roleFilter;

            let otpMatches = true;
            if (otpFilter === 'Verified') otpMatches = Boolean(u.otpVerified);
            if (otpFilter === 'Not Verified') otpMatches = !u.otpVerified;

            let searchMatches = true;
            if (q) {
                const firstName = (u.firstName || '').toLowerCase();
                const lastName = (u.lastName || '').toLowerCase();
                const email = (u.email || '').toLowerCase();
                const school = (u.schoolName || '').toLowerCase();
                const role = (u.role || '').toLowerCase();
                const otp = u.otpVerified ? 'verified' : 'not verified';

                searchMatches =
                    firstName.includes(q) ||
                    lastName.includes(q) ||
                    email.includes(q) ||
                    school.includes(q) ||
                    role.includes(q) ||
                    otp.includes(q);
            }

            return roleMatches && otpMatches && searchMatches;
        });
    }, [users, search, roleFilter, otpFilter]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h4" component="h2">
                    User Management
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadUsers}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search first name, last name, email, school, role, OTP status"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 380, flex: '1 1 380px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="role-filter-label">Role</InputLabel>
                    <Select
                        labelId="role-filter-label"
                        value={roleFilter}
                        label="Role"
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {roleOptions.map((roleName) => (
                            <MenuItem key={roleName} value={roleName}>{roleName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="otp-filter-label">OTP Status</InputLabel>
                    <Select
                        labelId="otp-filter-label"
                        value={otpFilter}
                        label="OTP Status"
                        onChange={(e) => setOtpFilter(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Verified">Verified</MenuItem>
                        <MenuItem value="Not Verified">Not Verified</MenuItem>
                    </Select>
                </FormControl>

                <Button variant="outlined" onClick={() => { setSearch(''); setRoleFilter('All'); setOtpFilter('All'); }}>
                    Reset
                </Button>

                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    {filteredUsers.length} result{filteredUsers.length === 1 ? '' : 's'}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, width: '100%', overflowX: 'auto' }}>
                <DataGrid
                    rows={filteredUsers}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        minWidth: 1500,
                        '& .MuiDataGrid-columnHeaderTitle': { display: 'block', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 700, textDecoration: 'underline' },
                        '& .MuiDataGrid-columnHeader .MuiChip-root, & .MuiDataGrid-columnHeader .MuiChip-label': { fontWeight: 400, textDecoration: 'none' },
                    }}
                />
            </Box>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user "{selectedUser?.firstName} {selectedUser?.lastName}" (ID: {selectedUser?.id})? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={verifyDialogOpen} onClose={handleVerifyDialogClose}>
                <DialogTitle>Manual OTP Verification</DialogTitle>
                <DialogContent>
                    <Typography>
                        Mark user "{selectedVerifyUser?.firstName} {selectedVerifyUser?.lastName}" (ID: {selectedVerifyUser?.id}) as OTP verified so they can continue login?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleVerifyDialogClose}>Cancel</Button>
                    <Button onClick={handleVerifyOtpConfirm} color="success" variant="contained">
                        Verify OTP
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={tempPasswordDialogOpen} onClose={handleTempPasswordDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Temporary Password</DialogTitle>
                <DialogContent>
                    {!generatedTempPassword ? (
                        <Typography>
                            Generate a one-time temporary password for "{selectedTempPasswordUser?.firstName} {selectedTempPasswordUser?.lastName}" (ID: {selectedTempPasswordUser?.id})?
                            Share it securely and ask them to sign in, then immediately change their password from the account page.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Typography>
                                Temporary password for {selectedTempPasswordUser?.email}:
                            </Typography>
                            <TextField
                                fullWidth
                                value={generatedTempPassword}
                                InputProps={{ readOnly: true }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                Instructions: Have the user sign in with this temporary password, then go to Update Account and change password right away.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleTempPasswordDialogClose}>Close</Button>
                    {!generatedTempPassword ? (
                        <Button onClick={handleTempPasswordConfirm} color="warning" variant="contained" disabled={tempPasswordLoading}>
                            {tempPasswordLoading ? 'Generating...' : 'Generate Temporary Password'}
                        </Button>
                    ) : (
                        <Button onClick={handleCopyTempPassword} color="primary" variant="outlined" startIcon={<ContentCopyIcon />}>
                            Copy Password
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserManagement;
